import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { themeConfig } from '../theme/themeConfig';
import { lightTheme, darkTheme, applyThemeVariables } from '../theme/accessibleTheme';

// Create Theme Context
const ThemeContext = createContext();

// Custom hook to use theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeContextProvider');
  }
  return context;
};

// Theme Context Provider Component
export const ThemeContextProvider = ({ children }) => {
  // Initialize theme mode from localStorage or default to 'light'
  const [themeMode, setThemeMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('jamDungJobsTheme');
      if (savedTheme) {
        return savedTheme;
      }
      // Check system preference
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
      }
    }
    return 'light';
  });

  // Get current theme object with accessibility improvements
  const currentTheme = themeMode === 'dark' ? darkTheme : lightTheme;
  const legacyTheme = themeConfig[themeMode]; // Keep for backward compatibility

  // Toggle theme function
  const toggleTheme = () => {
    const newMode = themeMode === 'light' ? 'dark' : 'light';
    setThemeMode(newMode);
  };

  // Set specific theme mode
  const setTheme = (mode) => {
    if (mode === 'light' || mode === 'dark') {
      setThemeMode(mode);
    }
  };

  // Save theme preference to localStorage and apply CSS variables
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('jamDungJobsTheme', themeMode);
      applyThemeVariables(themeMode);
      
      // Update document attributes for accessibility
      document.documentElement.setAttribute('data-theme', themeMode);
      document.documentElement.setAttribute('data-color-scheme', themeMode);
    }
  }, [themeMode]);

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      const handleChange = (e) => {
        // Only auto-switch if user hasn't manually set a preference
        const savedTheme = localStorage.getItem('jamDungJobsTheme');
        if (!savedTheme) {
          setThemeMode(e.matches ? 'dark' : 'light');
        }
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, []);

  // Context value with accessibility enhancements
  const contextValue = {
    themeMode,
    currentTheme,
    legacyTheme, // For backward compatibility
    toggleTheme,
    setTheme,
    isDarkMode: themeMode === 'dark',
    isLightMode: themeMode === 'light',
    jamaicanColors: themeConfig.jamaicanColors,
    // Accessibility features
    isHighContrast: window.matchMedia && window.matchMedia('(prefers-contrast: high)').matches,
    isReducedMotion: window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    // Theme validation
    isAccessible: true // Placeholder for theme validation
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={currentTheme}>
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContextProvider;
